import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, SelectChangeEvent, TextField, MenuItem, Select, OutlinedInput, Button, Grid2, Box, Autocomplete, Menu, Checkbox, FormControlLabel, Typography } from '@mui/material';import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { Employee, Positions } from "../types/EmployeeTypes";
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ConfirmBulkDeleteModal from './ConfirmBulkDeleteModal';
import ColumnFilterMenu from './ColumnFilterMenu';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditIcon from '@mui/icons-material/Edit';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

interface EmployeeTableProps {
  employees: Employee[];
  selectedProject: boolean;
  onEditEmployee: (oldRut: string, employee: Employee) => void;
  onDeleteEmployee: (employeeId: string) => void;
  /** Eliminación múltiple. Si no se entrega, la selección múltiple no se muestra. */
  onDeleteMultipleEmployees?: (employeeIds: string[]) => Promise<void> | void;
  projects: { id: number; name: string, costCenter: string }[];
  positions: Positions[];
  onDownloadFormat: () => void;
  //onClickImportEquipments: (event: Event) => void;
  onImportEmployees: (file: File) => void;
  onExportEmployees: () => void;
  reportDate?: string; // Recibimos la fecha para saber el modo
}

const EmployeeTable: React.FC<EmployeeTableProps> = ({
  employees,
  projects,
  positions,
  selectedProject,
  onEditEmployee,
  onDeleteEmployee,
  onDeleteMultipleEmployees,
  onDownloadFormat,
  //onClickImportEquipments,
  onExportEmployees,
  onImportEmployees,
  reportDate // Desestructuramos reportDate
}) => {
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);
  const [editedEmployeeData, setEditedEmployeeData] = useState<Employee | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('0');
  const [employeeName, setEmployeeName] = useState('');
  const [filter, setFilter] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState(employees);

  // Selección múltiple para eliminar. Solo en el mantenedor (no en modo histórico).
  const [selectedRuts, setSelectedRuts] = useState<string[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const allowMultiSelect = !reportDate && !!onDeleteMultipleEmployees;

  const [columnFilters, setColumnFilters] = useState({
        rut: [] as string[],
        name: [] as string[],
        firstName: [] as string[],
        lastName: [] as string[],
        position: [] as string[],
        project: [] as string[],
        costCenter: [] as string[],
        observations: [] as string[]
      });

  // Visibilidad de columnas (por defecto todas visibles). Permite mostrar/ocultar columnas.
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
        rut: true,
        name: true,
        firstName: true,
        lastName: true,
        position: true,
        project: true,
        costCenter: true,
        observations: true,
      });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);

  const toggleColumn = (id: string) => {
    setVisibleColumns(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Central de coste asociada al proyecto del empleado (asignación automática por central de coste)
  const getCostCenter = (employee: Employee) => employee.project?.costCenter || '';

  // Definición de columnas (cabeceras alineadas al Excel de importación)
  const columnsDefinition = [
    { id: 'rut', label: 'RUT/DNI' },
    { id: 'name', label: 'Nombre' },
    { id: 'firstName', label: 'Apellido Paterno' },
    { id: 'lastName', label: 'Apellido Materno' },
    { id: 'position', label: 'Cargo' },
    { id: 'project', label: 'Proyecto' },
    { id: 'costCenter', label: 'Central de Coste' },
    { id: 'observations', label: 'Observaciones' },
  ];

  // Columnas visibles según reportDate (oculta 'project' en histórico) y selección del usuario
  const isColumnVisible = (id: string) => {
    if (reportDate && id === 'project') return false;
    return visibleColumns[id] !== false;
  };

   const [filterMenu, setFilterMenu] = useState<{
    anchorEl: null | HTMLElement,
    columnKey: keyof typeof columnFilters | null
   }>({ anchorEl: null, columnKey: null });

  // Calcula los valores únicos para el menú de filtro que esté abierto
    const uniqueValues = React.useMemo<string[]>(() => {
      if (!filterMenu.columnKey) return [];

      let values: (string | null | undefined)[] = [];
      const key = filterMenu.columnKey;

      switch (key) {
        case 'rut':
          values = employees.map(emp => emp.rut);
          break;
        case 'name':
          values = employees.map(emp => emp.name);
          break;
        case 'firstName':
          values = employees.map(emp => emp.firstName);
          break;
        case 'lastName':
          values = employees.map(emp => emp.lastName);
          break;
        case 'position':
          values = employees.map(emp => emp.position || 'Sin Cargo');
          break;
        case 'project':
          values = employees.map(employee =>
            (employee.project && employee.status === 'assigned') ? (employee.project.name || 'Sin proyecto') :
            (employee.status === 'terminated' ? 'Desvinculado' :
            (employee.status === 'on_leave' ? 'Licencia Medica / Vacaciones' : 'Sin proyecto'))
          );
          break;
        case 'costCenter':
          values = employees.map(emp => getCostCenter(emp));
          break;
        case 'observations':
          values = employees.map(emp => emp.observations);
          break;
      }

      // Filtra nulos, ordena y obtén únicos
      return Array.from(new Set(values.filter((value): value is string => !!value))).sort();
    }, [employees, filterMenu.columnKey]);


  // Abre el menú de filtro
  const handleFilterMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    columnKey: keyof typeof columnFilters
  ) => {
    setFilterMenu({ anchorEl: event.currentTarget, columnKey });
  };

  // Cierra el menú
  const handleFilterMenuClose = () => {
    setFilterMenu({ anchorEl: null, columnKey: null });
  };

  // Recibe la selección ya calculada por el menú de filtro
  const handleColumnFilterChange = (selected: string[]) => {
    if (!filterMenu.columnKey) return;

    setColumnFilters(prevFilters => ({
      ...prevFilters,
      [filterMenu.columnKey as string]: selected,
    }));
  };

useEffect(() => {
    const globalSearch = filter.toLowerCase();

    // --- ETAPA 1: FILTRO GLOBAL (LÓGICA "O") ---
    const filteredByGlobal = employees.filter(employee => {
      if (!globalSearch) return true;
      const rutMatch = employee.rut?.toLowerCase().includes(globalSearch);
      const nameMatch = (employee.name + " " + employee.firstName + " " + employee.lastName)?.toLowerCase().includes(globalSearch);
      const projectDisplayGlobal = (employee.project && employee.status === 'assigned') ? (employee.project.name || 'Sin proyecto') :
                             (employee.status === 'terminated' ? 'Desvinculado' :
                             (employee.status === 'on_leave' ? 'Licencia Medica / Vacaciones' : 'Sin proyecto'));
      const projectMatch = projectDisplayGlobal?.toLowerCase().includes(globalSearch);
      return rutMatch || nameMatch || projectMatch;
    });

    // --- ETAPA 2: FILTRO POR COLUMNAS (LÓGICA "Y") ---
    const filteredByColumns = filteredByGlobal.filter(employee => {
      // Lógica de visualización para las columnas con menú
      const projectDisplayColumn = (employee.project && employee.status === 'assigned') ? (employee.project.name || 'Sin proyecto') :
                             (employee.status === 'terminated' ? 'Desvinculado' :
                             (employee.status === 'on_leave' ? 'Licencia Medica / Vacaciones' : 'Sin proyecto'));
      const positionDisplayColumn = employee.position || 'Sin Cargo';
      const costCenterDisplay = getCostCenter(employee);
      const rutDisplay = employee.rut || '';
      const nameDisplay = employee.name || '';
      const firstNameDisplay = employee.firstName || '';
      const lastNameDisplay = employee.lastName || '';
      const observationsDisplay = employee.observations || '';

      // Lógica de Filtro de Array para todas las columnas
      return (
        (columnFilters.rut.length === 0 || columnFilters.rut.includes(rutDisplay)) &&
        (columnFilters.name.length === 0 || columnFilters.name.includes(nameDisplay)) &&
        (columnFilters.firstName.length === 0 || columnFilters.firstName.includes(firstNameDisplay)) &&
        (columnFilters.lastName.length === 0 || columnFilters.lastName.includes(lastNameDisplay)) &&
        (columnFilters.position.length === 0 || columnFilters.position.includes(positionDisplayColumn)) &&
        (columnFilters.project.length === 0 || columnFilters.project.includes(projectDisplayColumn)) &&
        (columnFilters.costCenter.length === 0 || columnFilters.costCenter.includes(costCenterDisplay)) &&
        (columnFilters.observations.length === 0 || columnFilters.observations.includes(observationsDisplay))
      );
    });

    setFilteredEmployees(filteredByColumns);

  }, [filter, columnFilters, employees]);

  // La selección se limita siempre a las filas visibles: si un filtro oculta una fila
  // seleccionada, se deselecciona para no eliminar registros que el usuario no está viendo.
  useEffect(() => {
    setSelectedRuts(prev => {
      if (prev.length === 0) return prev;
      const visible = new Set(filteredEmployees.map(emp => emp.rut).filter(Boolean) as string[]);
      const next = prev.filter(rut => visible.has(rut));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredEmployees]);

  const visibleRuts = filteredEmployees.map(emp => emp.rut).filter(Boolean) as string[];
  const allVisibleSelected = visibleRuts.length > 0 && visibleRuts.every(rut => selectedRuts.includes(rut));
  const someVisibleSelected = selectedRuts.length > 0 && !allVisibleSelected;

  const toggleRowSelection = (rut: string) => {
    if (!rut) return;
    setSelectedRuts(prev => prev.includes(rut) ? prev.filter(item => item !== rut) : [...prev, rut]);
  };

  const toggleSelectAllVisible = () => {
    setSelectedRuts(allVisibleSelected ? [] : visibleRuts);
  };

  // Descripción de cada seleccionado para el modal de confirmación.
  const selectedDescriptions = filteredEmployees
    .filter(emp => selectedRuts.includes(emp.rut || ''))
    .map(emp => `${emp.rut} - ${[emp.name, emp.firstName, emp.lastName].filter(Boolean).join(' ')}`.trim());

  const handleConfirmBulkDelete = async () => {
    if (!onDeleteMultipleEmployees) return;
    setIsBulkDeleting(true);
    try {
      await onDeleteMultipleEmployees(selectedRuts);
      setSelectedRuts([]);
      setIsBulkDeleteModalOpen(false);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleEditClick = (employee: Employee) => {
    setEditingEmployeeId(employee.rut ?? null);
    setEditedEmployeeData(employee);
  };

  const handleOpenConfirmModal = (id: any, name: any) => {
    setEmployeeId(id);
    setEmployeeName(name);
    setIsConfirmModalOpen(true)
  }

  const handleSaveClick = () => {
    if (editedEmployeeData && editingEmployeeId) {
          onEditEmployee(editingEmployeeId, editedEmployeeData);
    }
    setEditingEmployeeId(null);
    setEditedEmployeeData(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editedEmployeeData) {
      const { name, value } = e.target;
      const finalValue = name === 'rut' ? value.toUpperCase() : value;

      setEditedEmployeeData({ ...editedEmployeeData, [name]: finalValue });
    }
  };

const handleProjectChange = (e: SelectChangeEvent<string | number>) => {
    if (editedEmployeeData) {
      const value = e.target.value;

      let newProject: Employee['project'] = editedEmployeeData.project;
      let newStatus: Employee['status'] = editedEmployeeData.status;

      if (value === 'Desvinculado') { // Valor del MenuItem
        newProject = null;
        newStatus = 'terminated'; // Estado en inglés
      } else if (value === 'Licencia_Vacaciones') { // Valor del MenuItem
        newProject = null;
        newStatus = 'on_leave'; // Estado en inglés
      } else if (value === '') {
        // Opción "Sin proyecto"
        newProject = null;
        newStatus = 'unassigned'; // Estado en inglés
      } else {
        // Es un ID de proyecto
        const projectId = Number(value);
        const projectSelected = projects.find(p => p.id === projectId);
        if (projectSelected) {
          newProject = {
              ...projectSelected,
              status: true // Valor por defecto para cumplir con la interfaz
          };
          newStatus = 'assigned';
        }
      }
      setEditedEmployeeData({
        ...editedEmployeeData,
        project: newProject,
        status: newStatus
      });
    }
  };

const getProjectValue = (employee: Employee | null): string | number => {
  if (!employee) return '';

  // 1. Si el proyecto existe y está asignado
  if (employee.project && employee.status === 'assigned') {
    return employee.project.id || '';
  }
  // 2. Si no hay proyecto, revisa el 'status'
  if (employee.status === 'terminated') {
    return 'Desvinculado'; // El valor del MenuItem
  }
  if (employee.status === 'on_leave') {
    return 'Licencia_Vacaciones'; // El valor del MenuItem
  }
  if (employee.status === 'unassigned') {
     return ''; // El valor del MenuItem "Sin proyecto"
  }
  return ''; // "Sin proyecto"
}

  const handlePositionChange = (event: React.SyntheticEvent, newValue: string | null | undefined) => {
    if (editedEmployeeData) {
      setEditedEmployeeData({
        ...editedEmployeeData,
        position: newValue || ''
      });
    }
  };

  const handleDeleteClick = (employeeIdParam: string) => {
    onDeleteEmployee(employeeId);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImportEmployees(file);
    }
    event.target.value = '';
  };

  const currentFilters = filterMenu.columnKey ? columnFilters[filterMenu.columnKey] : [];

  return (
    <Grid2>
      <TextField
        label="Buscar por Proyecto, RUT o nombre"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        fullWidth
        margin="normal"
      />

      {/* Barra de acciones de selección múltiple */}
      {allowMultiSelect && selectedRuts.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, p: 1.5, borderRadius: 1, backgroundColor: '#e3f2fd' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#004F86' }}>
            {selectedRuts.length === 1 ? '1 registro seleccionado' : `${selectedRuts.length} registros seleccionados`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => setSelectedRuts([])}>
              Quitar selección
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setIsBulkDeleteModalOpen(true)}
            >
              Eliminar seleccionados
            </Button>
          </Box>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ width: '100%', minWidth: 650, maxHeight: 700, overflow: 'auto' }} aria-label="employee table">
        <Table stickyHeader>
          <TableHead>
                      <TableRow>
                        {allowMultiSelect && (
                          <TableCell padding="checkbox" sx={{ verticalAlign: 'top', p: 1 }}>
                            <Checkbox
                              checked={allVisibleSelected}
                              indeterminate={someVisibleSelected}
                              onChange={toggleSelectAllVisible}
                              title="Seleccionar todo lo visible"
                            />
                          </TableCell>
                        )}
                        {columnsDefinition
                          .filter(col => isColumnVisible(col.id))
                          .map((col) => (
                          <TableCell key={col.id} sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {col.label}
                              <IconButton size="small" onClick={(e) => handleFilterMenuOpen(e, col.id as any)}
                                // @ts-ignore
                                sx={{ color: (columnFilters[col.id]?.length ?? 0) > 0 ? '#004F86' : 'inherit' }}
                              >
                                <FilterListIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        ))}
                        <TableCell sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top', p: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              title="Mostrar/ocultar columnas"
                              onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                              sx={{ color: '#004F86' }}
                            >
                              <ViewColumnIcon fontSize="small" />
                            </IconButton>
                            Acciones
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
          <TableBody>
            {filteredEmployees.map((employee) => ( // Usar filteredEmployees aquí
             <TableRow key={employee.rut} selected={selectedRuts.includes(employee.rut || '')}>
               {allowMultiSelect && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRuts.includes(employee.rut || '')}
                    onChange={() => toggleRowSelection(employee.rut || '')}
                    disabled={editingEmployeeId === employee.rut}
                  />
                </TableCell>
               )}
               {isColumnVisible('rut') && (
                <TableCell>
                  {editingEmployeeId === employee.rut ? (
                    <TextField
                      name="rut"
                      value={editedEmployeeData?.rut || ''}
                      onChange={handleInputChange}

                    />
                  ) : (
                    employee.rut
                  )}
                </TableCell>
               )}
                {isColumnVisible('name') && (
                <TableCell>
                  {editingEmployeeId === employee.rut ? (
                    <TextField
                      name="name"
                      value={editedEmployeeData?.name || ''}
                      onChange={handleInputChange}
                    />
                  ) : (
                    employee.name
                  )}
                </TableCell>
                )}
                {isColumnVisible('firstName') && (
                <TableCell>
                  {editingEmployeeId === employee.rut ? (
                    <TextField
                      name="firstName"
                      value={editedEmployeeData?.firstName || ''}
                      onChange={handleInputChange}
                    />
                  ) : (
                    employee.firstName
                  )}
                </TableCell>
                )}
                {isColumnVisible('lastName') && (
                <TableCell>
                  {editingEmployeeId === employee.rut ? (
                    <TextField
                      name="lastName"
                      value={editedEmployeeData?.lastName || ''}
                      onChange={handleInputChange}
                    />
                  ) : (
                    employee.lastName
                  )}
                </TableCell>
                )}
                {isColumnVisible('position') && (
                <TableCell>
                  {editingEmployeeId === employee.rut ? (
                    <Autocomplete
                      value={editedEmployeeData?.position || ''}
                      onChange={(event, newValue) => handlePositionChange(event, newValue)}
                      options={Array.from(new Set(positions.map((position) => position.description)))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cargo"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  ) : (
                    employee.position || 'Sin Cargo'
                  )}
                </TableCell>
                )}

                {/* Columna Proyecto (se oculta en modo histórico o si el usuario la oculta) */}
                {isColumnVisible('project') && (
                    <TableCell>
                    {editingEmployeeId === employee.rut ? (
                        <Select
                        name="projectId"
                        value={getProjectValue(editedEmployeeData)} // Usa el nuevo lector
                        onChange={handleProjectChange} // Usa el nuevo escritor
                        input={<OutlinedInput />}
                        fullWidth
                        displayEmpty
                        >
                        {projects.map((project) => (
                            <MenuItem key={project.id} value={project.id}>
                            {project.name}
                            </MenuItem>
                        ))}
                        <MenuItem value="">Sin proyecto</MenuItem>
                        <MenuItem value="Desvinculado">Desvinculado</MenuItem>
                        <MenuItem value="Licencia_Vacaciones">Licencia Medica / Vacaciones</MenuItem>
                        </Select>
                    ) : (

                        (employee.project && employee.status === 'assigned') ? employee.project.name :
                        (employee.status === 'terminated' ? 'Desvinculado' :
                        (employee.status === 'on_leave' ? 'Licencia Medica / Vacaciones' : 'Sin proyecto'))
                    )}
                    </TableCell>
                )}

                {/* Central de Coste: solo lectura, se asigna automáticamente por el proyecto */}
                {isColumnVisible('costCenter') && (
                    <TableCell>{getCostCenter(employee)}</TableCell>
                )}

                {isColumnVisible('observations') && (
                <TableCell>
                  {editingEmployeeId === employee.rut ? (
                    <TextField
                      name="observations"
                      value={editedEmployeeData?.observations || ''}
                      onChange={handleInputChange}
                    />
                  ) : (
                    employee.observations
                  )}
                </TableCell>
                )}
                <TableCell>
                  {editingEmployeeId === employee.rut ? (
                    <>
                      <IconButton onClick={handleSaveClick}>
                        <SaveIcon color="primary" />
                      </IconButton>
                      <IconButton color= "error" onClick={() => { setEditingEmployeeId(null); setEditedEmployeeData(null); }}>
                        Cancelar
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => handleEditClick(employee)}
                        disabled={!!reportDate} // Deshabilitado en modo histórico
                        sx={{ color: !!reportDate ? 'gray' : 'primary.main' }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => {
                          if (reportDate) {
                              onDeleteEmployee(employee.rut || ''); // Borrar del reporte
                          } else {
                              handleOpenConfirmModal(employee.rut || '', `${employee.name} ${employee.firstName} ${employee.lastName}`);
                          }
                      }}
                      title={!!reportDate ? "Quitar de este reporte diario" : "Eliminar empleado del sistema"}
                      >
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
     <ColumnFilterMenu
       anchorEl={filterMenu.anchorEl}
       values={uniqueValues}
       selected={currentFilters}
       onClose={handleFilterMenuClose}
       onChange={handleColumnFilterChange}
     />

      {/* Menú para mostrar/ocultar columnas */}
      <Menu
        anchorEl={columnsMenuAnchor}
        open={Boolean(columnsMenuAnchor)}
        onClose={() => setColumnsMenuAnchor(null)}
        PaperProps={{ style: { maxHeight: 400 } }}
      >
        {columnsDefinition
          .filter(col => !(reportDate && col.id === 'project'))
          .map((col) => (
          <MenuItem key={col.id} onClick={() => toggleColumn(col.id)}>
            <FormControlLabel
              control={<Checkbox checked={visibleColumns[col.id] !== false} />}
              label={col.label}
              sx={{ pointerEvents: 'none' }}
            />
          </MenuItem>
        ))}
      </Menu>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="contained" color="primary" onClick={onDownloadFormat}>
          Descargar Formato
        </Button>
        <input
          accept=".xlsx, .xls"
          style={{ display: 'none' }}
          id="import-file"
          type="file" //@ts-ignore
          //onClick={onClickImportEquipments}
          onChange={handleFileChange}
        />
        <label htmlFor="import-file">
          <Button variant="contained" color="primary" component="span">
            Importar Personal
          </Button>
        </label>

        <Button variant="contained" color="primary" onClick={onExportEmployees}>
          Exportar Personal
        </Button>
      </Box>
      <ConfirmDeleteModal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}
        onSubmit={handleDeleteClick} id={employeeId} name={employeeName} title={'EMPLOYEE'}></ConfirmDeleteModal>

      <ConfirmBulkDeleteModal
        open={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        items={selectedDescriptions}
        entityLabel="empleado"
        entityLabelPlural="empleados"
        loading={isBulkDeleting}
      />
    </Grid2>

  );
};

export default EmployeeTable;